import Categories from '@/components/categories/categories';
import SectionComponent from '@/components/layouts/section-component';
import SectionsLoader from '@/components/ui/loaders/sections-loader';
import { useSections } from '@/framework/sections';
import { Section } from '@/types';
import uniqueId from 'lodash/uniqueId';
import dynamic from 'next/dynamic';
import { useEffect, useRef } from 'react';

const ErrorMessage = dynamic(() => import('@/components/ui/error-message'));

const Sections: React.FC<any> = ({ categories }) => {
  const {
    data,
    isLoading,
    error,
    isFetching,
    isLoadingMore,
    loadMore,
    hasMore,
  } = useSections();

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !isLoadingMore && !isFetching) {
          loadMore();
        }
      },
      {
        rootMargin: '200px',
      },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasMore, isLoadingMore, isFetching, loadMore]);

  if (isLoading) {
    return (
      <div className="m-4 mb-0">
        <SectionsLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-16 animate-pulse">
        <ErrorMessage message={error.message ?? 'error-section-load'} />
      </div>
    );
  }

  if (!data?.length) {
    return <Categories layout="minimal" variables={categories} />;
  }

  return (
    <div className="m-4 mb-0">
      {data.map((section: Section) => (
        <SectionComponent key={uniqueId('section-')} section={section} />
      ))}

      {hasMore && <div ref={loadMoreRef}></div>}
    </div>
  );
};

export default Sections;
